import { type ImageFormats } from '@components/helpers/imagesImports';

const Products = {
  AIRPLANE: 'airplane',
  SHIP: 'ship',
  CAR: 'car',
  GUITAR: 'guitar',
  ELECTRICGUITAR: 'electricguitar',
  DJCONTROLLER: 'gjcontroller',
  BALL: 'ball',
  TOOLS: 'tools',
  POWERDRILL: 'powerdrill',
  MACHINE: 'machine',
  TECHNIQUE: 'technique',
  AIR: 'air',
  PHONE: 'phone',
  ROBOT: 'robot',
  GLASSES: 'glasses',
  WINDOW: 'window',
  PENCIL: 'pencil',
  SPORT: 'sport',
  MIRROR: 'mirror',
  SOFTTOY: 'softtoy',
  BOARDGAME: 'boardgame',
  ENERGY: 'energy',
  JEWELRY: 'jewelry',
  PLACTICTOY: 'plastictoy',
  BOOK: 'book',
  CLOTHING: 'clothing',
  VASE: 'vase',
  ARMCHAIR: 'armchair',
  DESKLAMP: 'desklamp',
  FURNITURE: 'furniture',
  SNEAKERS: 'sneakers',
  BAG: 'bag',
  PRINTING: 'printing',
  GLOVE: 'glove',
  SHOVEL: 'shovel',
} as const;

type ProductsType = (typeof Products)[keyof typeof Products];

// Define a type for each category's icons

type FurnitureGoods = {
  furniture: string;
  garden_furniture: string;
  office_furniture: string;
  upholstered_furniture: string;
};

type GarmentGoods = {
  accessories: string;
  clothing: string;
  footwear: string;
  sports_equipment: string;
};

type PrintingGoods = {
  books: string;
  packaging: string;
  photo_frames: string;
  stationery: string;
};

type HomewareGoods = {
  electric_fireplaces: string;
  home_textiles: string;
  lamps: string;
  tableware: string;
};

type ToolmakingGoods = {
  electronic_tools: string;
  garden_tools: string;
  hand_tools: string;
  machines: string;
};

type ChildrenGoods = {
  board_games: string;
  construction_toys: string;
  electronic_toys: string;
  soft_toys: string;
};

type MusicalGoods = {
  electric_guitar: string;
  guitar: string;
  light_music: string;
  speakers: string;
};

type ElectronicsGoods = {
  laptop: string;
  robotics: string;
  vr_equipment: string;
  washing_machine: string;
};

type MachineryGoods = {
  airplane: string;
  car: string;
  ship: string;
  train: string;
};

// Define a generic category type
type FactoryCategory<GoodsType> = {
  character: ImageFormats;
  background: ImageFormats;
  build: ImageFormats;
  icons: GoodsType;
};

// Define the main FactoryType
type FactoryTypeImages = {
  furniture_factory: FactoryCategory<FurnitureGoods>;
  garment_factory: FactoryCategory<GarmentGoods>;
  printing_factory: FactoryCategory<PrintingGoods>;
  homeware: FactoryCategory<HomewareGoods>;
  toolmaking: FactoryCategory<ToolmakingGoods>;
  childrens_goods: FactoryCategory<ChildrenGoods>;
  musical_equipment: FactoryCategory<MusicalGoods>;
  electronics: FactoryCategory<ElectronicsGoods>;
  machinery: FactoryCategory<MachineryGoods>;
};

type FactoryTypeText =
  | 'furniture_factory'
  | 'garment_factory'
  | 'printing_factory'
  | 'homeware'
  | 'toolmaking'
  | 'childrens_goods'
  | 'musical_equipment'
  | 'electronics'
  | 'machinery';

const factoryTypesArray: FactoryTypeText[] = [
  'furniture_factory',
  'garment_factory',
  'printing_factory',
  'homeware',
  'toolmaking',
  'childrens_goods',
  'musical_equipment',
  'electronics',
  'machinery',
];

const factoryDisplayNames: Record<FactoryTypeText, string> = {
  furniture_factory: 'Furniture factory',
  garment_factory: 'Sewing factory',
  printing_factory: 'Print production',
  homeware: 'Home goods',
  toolmaking: 'Tools',
  childrens_goods: "Children's goods",
  musical_equipment: 'Musical instruments',
  electronics: 'Electronics',
  machinery: 'Mechanical engineering',
};

export {
  type FactoryCategory,
  factoryDisplayNames,
  type FactoryTypeImages,
  factoryTypesArray,
  type FactoryTypeText,
  Products,
  type ProductsType,
};
